import { useEffect } from 'react';
import { I18n } from '@aws-amplify/core';
import { useResolution886Report } from '@/context/Resolution886Report';
import { AdditionalEvaluation } from '@/components/views/Resolution886/enums';
import {
	useCreateAssessmentAdditionalEvaluation,
	useUpdateAssessmentAdditionalEvaluation
} from '@/hooks/v2/resolution-886';
import type { RelatedReport } from '@/context/Resolution886Report/types';

enum VibrationToolNames {
	HAND_ARM_VIBRATION = 'hand_arm_vibration',
	WHOLE_BODY_VIBRATION = 'whole_body_vibration'
}

interface UseVibrationToolsProps {
	name: string;
	categoryKey: string;
	relatedReport: RelatedReport | null;
}

export function useVibrationTools({ relatedReport, categoryKey, name }: Readonly<UseVibrationToolsProps>) {
	const { form, assessment, handleSetRelatedReport } = useResolution886Report();

	const isUpdating: boolean = !!relatedReport?.result?.id;
	const isDisabled: boolean = !!relatedReport?.result?.custom_report_result_id;
	const isWholeBodyVibration: boolean = categoryKey === 'whole_body_vibration';

	const additionalEvaluation =
		categoryKey === VibrationToolNames.HAND_ARM_VIBRATION
			? AdditionalEvaluation.HAND_ARM_VIBRATION_EXPOSURE
			: AdditionalEvaluation.WHOLE_BODY_VIBRATION_EXPOSURE;

	const organization_id = form.getFieldValue(['general_information', 'organization_id']);
	const company_id = form.getFieldValue(['general_information', 'company_id']);

	const toolMsg = I18n.get('ErgonomicVibrationTools886').replace('{{tool_name}}', name || '');

	const { mutate: createAssessmentAdditionalEvaluation } = useCreateAssessmentAdditionalEvaluation();
	const { mutate: updateAssessmentAdditionalEvaluation } = useUpdateAssessmentAdditionalEvaluation();

	function mountPayload() {
		const additionalEvaluationOption = form.getFieldValue(['jds_d86', 'additional_evaluation_option']);
		const value = form.getFieldValue(['jds_d86', 'value']);

		const payload = {
			organization_id,
			company_id,
			assessment_id: assessment.id,
			additional_evaluation: additionalEvaluation,
			value,
			additional_evaluation_option: null
		};

		if (additionalEvaluation === AdditionalEvaluation.WHOLE_BODY_VIBRATION_EXPOSURE) {
			payload.additional_evaluation_option = additionalEvaluationOption;
		}

		return payload;
	}

	function handleCreateAdditionalEvaluation(): void {
		const payload = mountPayload();

		createAssessmentAdditionalEvaluation(payload, {
			onSuccess: ({ data }) => {
				handleMapDataToRelatedReport(data);
			}
		});
	}

	function handleUpdateAdditionalEvaluation(): void {
		if (!relatedReport?.result?.id) {
			return;
		}

		const payload = mountPayload();

		updateAssessmentAdditionalEvaluation(
			{
				...payload,
				assessment_additional_evaluation_id: relatedReport?.result?.id
			},
			{
				onSuccess: ({ data }) => handleMapDataToRelatedReport(data)
			}
		);
	}

	function handleMapDataToRelatedReport(data: any) {
		handleSetRelatedReport({
			name: data.additional_evaluation,
			result: {
				id: data.id,
				risk: data.risk,
				custom_report_result_id: data.custom_report_result_id || null,
				additional_evaluation_option: data.additional_evaluation_option || undefined,
				value: data.value
			}
		});
	}

	function handleOnChange(): void {
		const additionalEvaluationOption = form.getFieldValue(['jds_d86', 'additional_evaluation_option']);

		if (isWholeBodyVibration && !additionalEvaluationOption) {
			return;
		}

		isUpdating ? handleUpdateAdditionalEvaluation() : handleCreateAdditionalEvaluation();
	}

	function handleOnClick(): void {
		const customReportResultId: string | undefined = relatedReport?.result?.custom_report_result_id;

		let path: string = `/custom-reports/jds-d86/report`;

		if (customReportResultId) {
			path += `/${customReportResultId}`;
		}

		window.open(path, '_blank');
	}

	useEffect(() => {
		if (relatedReport?.result?.value) {
			form.setFieldsValue({
				jds_d86: {
					value: Number(relatedReport?.result?.value),
					additional_evaluation_option: relatedReport?.result?.additional_evaluation_option
				}
			});
		}
	}, [relatedReport]);

	return {
		toolMsg,
		isDisabled,
		categoryKey,
		relatedReport,
		isWholeBodyVibration,
		handleCreateAdditionalEvaluation,
		handleUpdateAdditionalEvaluation,
		handleChange: handleOnChange,
		handleOnClick
	};
}
